import { FetchError } from 'ofetch'

export interface LoginFormSignIn {
  customerNo: string
  username: string
  password: string
  acceptTerms: boolean
}

/**
 * Composable for managing user authentication state.
 */
export function useAuth() {
  const userSession = useUserSession()
  const runtimeConfig = useRuntimeConfig()
  const routePath = useRoutePath()
  const { $csrfFetch, $i18n } = useNuxtApp()

  const loggedIn = computed(() => {
    if (runtimeConfig.public.mockFlow.active) {
      return true
    }

    return userSession.loggedIn.value
  })

  async function loginSignIn(form: LoginFormSignIn): Promise<Result<void>> {
    try {
      await $csrfFetch('/api/auth/login', {
        method: 'POST',
        body: form,
      })
      await refreshSession()
    } catch (error) {
      return errFromException(error)
    }

    return ok()
  }

  async function startSSOLogin() {
    if (!runtimeConfig.public.cmsLoginSSOUrl) {
      throw new Error('SSO login URL is not configured.')
    }

    await navigateTo(runtimeConfig.public.cmsLoginSSOUrl, {
      external: true,
    })
  }

  async function finalizeSSOLogin(): Promise<Result<void>> {
    try {
      await $csrfFetch('/api/auth/login-sso', {
        method: 'POST',
      })
      await refreshSession()
    } catch (error) {
      return errFromException(error)
    }

    return ok()
  }

  async function doBackendAccessTokenLogin(params: { accessToken: string }): Promise<Result<void>> {
    try {
      await $csrfFetch('/api/auth/backend-login', {
        method: 'POST',
        body: {
          accessToken: params.accessToken,
        },
      })
      await refreshSession()
    } catch (error) {
      return errFromException(error)
    }

    return ok()
  }

  async function refreshSession() {
    await userSession.fetch()
  }

  async function logout(): Promise<Result<void>> {
    if (runtimeConfig.public.mockFlow.active) {
      await navigateTo(routePath.login())
      return ok()
    }

    try {
      await $csrfFetch('/api/auth/logout', {
        method: 'POST',
      })
    } catch (error) {
      return errFromException(error)
    }

    window.location.href = routePath.login()

    return ok()
  }

  async function requestPasswordReset(email: string): Promise<Result<void>> {
    try {
      await $csrfFetch('/api/auth/password-reset-request', {
        method: 'POST',
        body: {
          email,
          locale: $i18n.locale.value,
        },
      })
    } catch (error) {
      return errFromException(error)
    }

    return ok()
  }

  async function changePassword(params: {
    password: string
    passwordRepeat: string
    token: string
  }): Promise<Result<{ validationError: string | null }>> {
    try {
      await $csrfFetch('/api/auth/change-password', {
        method: 'POST',
        body: {
          password: params.password,
          passwordRepeat: params.passwordRepeat,
          token: params.token,
          locale: $i18n.locale.value,
        },
      })
    } catch (error: unknown) {
      if (error instanceof FetchError && error.statusCode === 422) {
        return ok({
          validationError: error.data?.data.error ?? null,
        })
      }
      return errFromException(error)
    }

    return ok({ validationError: null })
  }

  return {
    loggedIn,
    loginSignIn,
    startSSOLogin,
    finalizeSSOLogin,
    doBackendAccessTokenLogin,
    requestPasswordReset,
    changePassword,
    refreshSession,
    logout,
  }
}
